import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr, money, fdt } from "../api";
import { useAuth } from "../context/AuthContext";
import { Banknote } from "lucide-react";
import { Stat } from "./bits";

const btnBlack = "bg-[#1C1B1A] text-white text-xs font-semibold px-4 py-2 rounded-full hover:bg-[#2C2B2A] transition-all disabled:opacity-40";

export default function JobInvoices() {
  const { user } = useAuth();
  const isCompany = user?.role === "company" || user?.role === "admin";
  const [invoices, setInvoices] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(() => {
    api.get("/billing/job-invoices").then((r) => setInvoices(r.data.invoices)).catch(() => setInvoices([]));
  }, []);
  useEffect(load, [load]);

  const act = async (inv, action) => {
    setBusyId(inv.id);
    try {
      await api.post(`/jobs/${inv.job_id}/invoice/${action}`);
      toast.success(action === "mark-paid" ? "Marked as paid" : "Receipt confirmed — settled");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusyId("");
  };

  if (invoices === null) return <p className="text-xs text-[#A09D95] py-10 text-center">Loading…</p>;

  const shown = invoices.filter((i) => filter === "all" || i.status === filter);
  const open = invoices.filter((i) => i.status !== "confirmed");
  const openByCur = open.reduce((acc, i) => { acc[i.currency] = (acc[i.currency] || 0) + i.amount; return acc; }, {});

  const statusLabel = (i) => i.status === "confirmed" ? "Settled"
    : i.status === "paid" ? (isCompany ? "Paid — awaiting confirmation" : "Paid — confirm receipt")
    : (isCompany ? "To pay" : "Awaiting payment");

  return (
    <div className="space-y-4" data-testid="job-invoices">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={isCompany ? "To pay" : "To receive"} value={open.length} sub="open invoices" />
        {Object.entries(openByCur).map(([cur, amt]) => (
          <Stat key={cur} label={`Open ${cur}`} value={money(amt, cur)} sub="ex VAT" />
        ))}
        <Stat label="Settled" value={invoices.filter((i) => i.status === "confirmed").length} sub="completed payments" />
      </div>

      <div className="inline-flex bg-[#EFECE2] p-1 rounded-full gap-1 flex-wrap">
        {[["all", "All"], ["issued", isCompany ? "To pay" : "Awaiting payment"], ["paid", "Paid"], ["confirmed", "Settled"]].map(([v, l]) => (
          <button key={v} data-testid={`jobinv-filter-${v}`} onClick={() => setFilter(v)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${filter === v ? "bg-[#1C1B1A] text-white" : "text-[#78756E]"}`}>{l}</button>
        ))}
      </div>

      <div className="cr-card !p-0 cr-table-wrap">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[#A09D95] border-b border-[#E8E4D9]">
            <th className="p-3 font-semibold">Issued</th>
            <th className="p-3 font-semibold">Job</th>
            <th className="p-3 font-semibold">Route</th>
            <th className="p-3 font-semibold">{isCompany ? "Courier" : "Company"}</th>
            <th className="p-3 font-semibold">Amount (ex VAT)</th>
            <th className="p-3 font-semibold">Status</th>
            <th className="p-3 font-semibold text-right">Action</th>
          </tr></thead>
          <tbody>
            {shown.map((i) => (
              <tr key={i.id} className="border-b border-[#F6F3EA] last:border-0 hover:bg-[#FCFAF5] transition-colors" data-testid={`job-invoice-${i.id}`}>
                <td className="p-3 text-[#78756E] whitespace-nowrap">{fdt(i.issued_at)}</td>
                <td className="p-3"><Link to={`/jobs/${i.job_id}`} className="font-mono font-semibold hover:underline">{i.ref}</Link></td>
                <td className="p-3 whitespace-nowrap">{i.route || "—"}</td>
                <td className="p-3">{isCompany ? (i.courier_name || "—") : (i.company_name || "—")}</td>
                <td className="p-3 font-semibold whitespace-nowrap">{money(i.amount, i.currency)}</td>
                <td className="p-3">
                  <span data-testid={`jobinv-status-${i.id}`} className={i.status === "confirmed" ? "cr-pill-dark" : i.status === "paid" ? "cr-pill-yellow" : "cr-pill-outline"}>
                    {statusLabel(i)}
                  </span>
                </td>
                <td className="p-3 text-right">
                  {isCompany && i.status === "issued" && (
                    <button data-testid={`invoice-pay-${i.id}`} disabled={busyId === i.id} onClick={() => act(i, "mark-paid")} className={btnBlack}>
                      <Banknote className="w-3.5 h-3.5 inline mr-1" />Mark paid
                    </button>
                  )}
                  {!isCompany && i.status === "paid" && (
                    <button data-testid={`invoice-confirm-${i.id}`} disabled={busyId === i.id} onClick={() => act(i, "confirm-received")} className={btnBlack}>
                      Confirm received
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-[#A09D95]">
                No job invoices here yet — they're created automatically when a delivery is completed.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[#A09D95]">
        {isCompany
          ? "Pay the courier directly, then mark the invoice paid. The courier confirms receipt to settle."
          : "When the company marks an invoice paid, confirm once the money arrives to settle it."}
      </p>
    </div>
  );
}
